import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { API_BASE_URL } from './apiConfig';

/**
 * API Client — axios 实例
 *
 * - 请求拦截器：从 localStorage 注入 Access Token
 * - 响应拦截器：401 自动刷新、统一格式拆包
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// ─── 请求拦截器：从 localStorage 注入 Access Token ───────────────────────
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    try {
      const token = localStorage.getItem('access_token');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      // 忽略存储异常
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── 响应拦截器 ─────────────────────────────────────────────────────────
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const res = response.data;
    if (res && typeof res === 'object') {
      if (res.code !== undefined && res.code !== 0 && res.code !== 200) {
        return Promise.reject(new Error(res.message || '请求失败'));
      }
      if ('data' in res) return res.data;
    }
    return res;
  },
  async (error) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // 1) 401 — 尝试 refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken,
          });
          localStorage.setItem('access_token', data.accessToken);
          localStorage.setItem('refresh_token', data.refreshToken);
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
          }
          return apiClient(originalRequest);
        }
      } catch {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        if (window.location.pathname !== '/login') {
          window.location.href = '/#/login';
        }
        return Promise.reject(new Error('登录已过期，请重新登录'));
      }
    }

    // 2) 其它错误 → 友好提示
    const errData = error.response?.data;
    const status = error.response?.status;
    let message = errData?.message || errData?.error || error.message || '网络请求异常，请稍后重试';
    if (status === 403) message = '没有权限执行此操作';
    else if (status === 404) message = '请求的资源不存在';
    else if (status === 500) message = '服务器内部错误，请稍后重试';
    else if (status === 429) message = '请求过于频繁，请稍后重试';

    return Promise.reject(new Error(message));
  }
);

export default apiClient;
